import * as React from "react";
import { useQuery } from "@apollo/client";
import { gettext } from "i18n";
import { useAuth } from "state/auth";
import {
  GetManagedStationList,
  GetManagedStationListVariables,
} from "utils/__generated__/GetManagedStationList";
import { GetStationList } from "utils/__generated__/GetStationList";
import { GET_MANAGED_STATION_LIST, GET_STATION_LIST } from "utils/stations";
import { SearchableDropdown, DropdownOption } from "./SearchableDropdown";

export interface SearchableStationPickerProps {
  onChange: (value: string) => void;
  value: string;
  byIdentifier?: boolean;
  filter?: (station: SearchableStation) => boolean;
  placeholder?: string;
  useFixedPositioning?: boolean;
  onStationSelect?: (station: SearchableStation | null) => void;
  organizationId?: number;
  includeWithoutOrganization?: boolean;
  checkCoordinates?: boolean;
}

export interface SearchableStation {
  id: number;
  identifier: string;
  name: string;
  organization_id?: number | null;
  lat?: number | null;
  lon?: number | null;
}

export const SearchableStationPicker: React.FC<
  SearchableStationPickerProps
> = ({
  onChange,
  value,
  byIdentifier = false,
  filter = () => true,
  placeholder,
  useFixedPositioning = true,
  onStationSelect,
  organizationId,
  includeWithoutOrganization = false,
  checkCoordinates = false,
}) => {
  const { user } = useAuth();
  const stationQuery = user?.admin
    ? GET_STATION_LIST
    : GET_MANAGED_STATION_LIST;
  const { data, loading } = useQuery<
    GetManagedStationList | GetStationList,
    GetManagedStationListVariables
  >(stationQuery, {
    variables: {
      userId: user?.id ?? 0,
    },
  });

  const stations = React.useMemo(() => {
    const stationList = [...(data?.station ?? [])];
    stationList.sort((a, b) => a.name.localeCompare(b.name));
    const filteredByOrg = organizationId
      ? stationList.filter(
          (s) =>
            s.organization_id === organizationId ||
            (includeWithoutOrganization &&
              (s.organization_id === null || s.organization_id === undefined)),
        )
      : stationList;
    return filteredByOrg.filter(filter);
  }, [data?.station, filter, organizationId, includeWithoutOrganization]);

  const options: DropdownOption[] = React.useMemo(() => {
    return stations.map((station) => {
      const hasNoCoordinates =
        station.lat === null ||
        station.lat === undefined ||
        station.lon === null ||
        station.lon === undefined;
      return {
        value: byIdentifier ? String(station.identifier) : String(station.id),
        label: `${station.name} (${station.identifier})`,
        warning: checkCoordinates && hasNoCoordinates,
      };
    });
  }, [stations, byIdentifier, checkCoordinates]);

  const handleChange = React.useCallback(
    (selectedValue: string) => {
      onChange(selectedValue);
      if (onStationSelect) {
        const selectedStation = stations.find((s) =>
          byIdentifier
            ? String(s.identifier) === selectedValue
            : String(s.id) === selectedValue,
        );
        onStationSelect(selectedStation ?? null);
      }
    },
    [onChange, onStationSelect, stations, byIdentifier],
  );

  if (loading) {
    return <div>{gettext("Loading stations...")}</div>;
  }

  if (stations.length === 0) {
    return <div>{gettext("No stations available")}</div>;
  }

  return (
    <SearchableDropdown
      placeholder={placeholder ?? gettext("Select station...")}
      value={value}
      options={options}
      onChange={handleChange}
      useFixedPositioning={useFixedPositioning}
    />
  );
};
